// Set MPIN: a step-up OTP (MPIN_SET) goes to the registered mobile, the
// customer enters the code, then a new four-digit MPIN twice, and the MPIN is
// sent with the code to /auth/mpin/set. This sets a first MPIN, replaces an
// existing one and clears an MPIN lock. ?return= names the page to go back to.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { generateStepUpOtp, setMpin, toFriendlyOtpError } from '@/api/auth';
import { ApiError } from '@/api/client';
import { MPIN_LENGTH, confirmMpinError, newMpinError } from '@/utils/phase2Validation';
import Button from '@/components/ui/Button/Button';
import OtpInput from '@/components/ui/OtpInput/OtpInput';
import Loader from '@/components/ui/Loader/Loader';
import PinPad from '@/components/common/auth/PinPad';
import './profile.css';

type Step = 'CODE' | 'NEW' | 'CONFIRM' | 'DONE';

const DEFAULT_RETURN = '/profile/settings';

// A wrong, expired or used-up code sends the customer back to the code step.
const CODE_ERRORS = ['OTP_001', 'OTP_003', 'OTP_002', 'OTP_006'];

/** Follows only paths on this site; anything else goes to app settings. */
const safeReturnPath = (value: string | null): string =>
  value && /^\/(?![/\\])/.test(value) ? value : DEFAULT_RETURN;

const errorMessage = (err: unknown, fallback: string): string => {
  const friendly = toFriendlyOtpError(err);
  return friendly instanceof ApiError ? friendly.message : fallback;
};

const SetMpin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get('return'));

  const [step, setStep] = useState<Step>('CODE');
  const [otpTransactionId, setOtpTransactionId] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [newMpin, setNewMpin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinResetKey, setPinResetKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const firstSendRef = useRef(false);

  const sendCode = useCallback(async () => {
    setSending(true);
    setCodeError('');
    setCode('');
    try {
      const session = await generateStepUpOtp({ transactionType: 'MPIN_SET' });
      setOtpTransactionId(session.transactionId);
      setMaskedDestination(session.maskedDestination ?? '');
    } catch (err) {
      setCodeError(errorMessage(err, 'Could not send the code. Try again.'));
    } finally {
      setSending(false);
    }
  }, []);

  // Send the first code when the page opens (once, even in StrictMode).
  useEffect(() => {
    if (firstSendRef.current) return;
    firstSendRef.current = true;
    void sendCode();
  }, [sendCode]);

  const failPad = (message: string) => {
    setPinError(message);
    setPinResetKey((k) => k + 1);
  };

  const goToNewMpin = () => {
    setPinError('');
    setStep('NEW');
  };

  const handleNewMpin = (pin: string) => {
    const error = newMpinError(pin);
    if (error) {
      failPad(error);
      return;
    }
    setNewMpin(pin);
    setPinError('');
    setStep('CONFIRM');
  };

  const handleConfirmMpin = async (pin: string) => {
    const mismatch = confirmMpinError(newMpin, pin);
    if (mismatch) {
      failPad(mismatch);
      setStep('NEW');
      return;
    }
    setSaving(true);
    try {
      await setMpin(newMpin, otpTransactionId, code);
      setStep('DONE');
    } catch (err) {
      if (err instanceof ApiError && CODE_ERRORS.includes(err.code)) {
        setCode('');
        setCodeError(errorMessage(err, 'That code did not work. Try again.'));
        setStep('CODE');
      } else {
        // A weak MPIN or any other refusal: show the server's message and
        // ask for a new MPIN again.
        failPad(errorMessage(err, 'Could not set the MPIN. Try again.'));
        setStep('NEW');
      }
    } finally {
      setSaving(false);
    }
  };

  const backToCode = () => {
    setPinError('');
    setStep('CODE');
  };

  const firstSendPending = sending && !otpTransactionId;

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => navigate(returnPath)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Set MPIN</h2>
      </div>

      {step !== 'DONE' && (
        <>
          <p className="nova-review-note">
            Your MPIN approves payments. Setting a new one replaces your old MPIN and unlocks a locked
            MPIN.
          </p>

          {firstSendPending && (
            <>
              <p>Sending a code to your registered mobile…</p>
              <Loader rows={2} />
            </>
          )}

          {!firstSendPending && (
            <>
              {otpTransactionId && (
                <p>
                  Enter the 6-digit code sent to {maskedDestination || 'your registered mobile'}.
                </p>
              )}
              {otpTransactionId && (
                <OtpInput
                  value={code}
                  onChange={(value) => {
                    setCode(value);
                    setCodeError('');
                  }}
                  error={codeError}
                />
              )}
              {!otpTransactionId && codeError && (
                <div className="nova-limit-box">{codeError}</div>
              )}

              <div className="nova-auth-timer mb-3">
                <button
                  type="button"
                  className="nova-auth-resend"
                  onClick={() => void sendCode()}
                  disabled={sending}
                >
                  {sending ? 'Sending…' : otpTransactionId ? 'Resend code' : 'Send code'}
                </button>
              </div>

              <Button
                onClick={goToNewMpin}
                disabled={!otpTransactionId || code.length !== 6 || sending}
              >
                Continue
              </Button>
            </>
          )}
        </>
      )}

      {step === 'DONE' && (
        <div className="nova-profile-success">
          <FontAwesomeIcon icon={faCircleCheck} size="3x" className="nova-outcome-ok" />
          <h3>Your MPIN is set</h3>
          <p>Use it to approve your payments.</p>
          <Button onClick={() => navigate(returnPath, { replace: true })}>
            {returnPath.startsWith('/pay') ? 'Back to payment' : 'Done'}
          </Button>
        </div>
      )}

      <PinPad
        show={step === 'NEW'}
        variant="MPIN"
        title="Enter a new MPIN"
        subtitle="4 digits. Not one digit repeated or a straight run like 1234."
        pinLength={MPIN_LENGTH}
        error={pinError}
        resetKey={pinResetKey}
        onComplete={handleNewMpin}
        onClose={backToCode}
      />
      <PinPad
        show={step === 'CONFIRM'}
        variant="MPIN"
        title="Re-enter the new MPIN"
        pinLength={MPIN_LENGTH}
        error={pinError}
        busy={saving}
        resetKey={pinResetKey}
        onComplete={(pin) => void handleConfirmMpin(pin)}
        onClose={backToCode}
      />
    </div>
  );
};

export default SetMpin;
